import { HourglassEmptyOutlined } from '@mui/icons-material';
import { Avatar } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { useBooth } from '../../../../hooks/useBooth';
import { useMeeting } from '../../../../hooks/useMeeting';
import { usePermission } from '../../../../hooks/usePermission';
import { Booth, Meetingroom } from '../../../../interfaces/interfaces';
import { RootState } from '../../../../reduxToolkit/store';
import { useSelector } from '../../../../reduxToolkit/typedRedux';
import { createDisplayLink, formatUTCDate } from '../../../../services/helper';
import VyTooltip from '../../../common/VyTooltip';
import { VYSTEMBLUE } from '../../../../static/constants';

interface MeetingRoomTemplateProps {
  meetingroom: Meetingroom;
  booth: Booth;
}

const MeetingRoomTemplate = ({ meetingroom, booth }: MeetingRoomTemplateProps) => {
  const displaystatus = useSelector(({ common }: RootState) => common.meetingroomStatuses[meetingroom._id]);
  const event = useSelector(({ common }: RootState) => common.event);
  const user = useSelector(({ common }) => common.user);

  const { t } = useTranslation(['booth', 'common']);
  const { hasEditPrivilege } = useBooth();

  const status = displaystatus || meetingroom.displaystatus;
  const { join } = useMeeting();
  const { isEventHost, isBoothHost } = usePermission();

  function getStartTime() {
    return meetingroom.status.islive ? t('booth:live-now') : `${formatUTCDate(new Date(meetingroom?.slot?.datetime_start), 'HH:mm')} ${t('common:clock')}`;
  }

  const isPrivileged = !!user && hasEditPrivilege(booth, user) && isBoothHost(user, [booth!]);

  function buttonText() {
    if (meetingroom.meetingtype === 'presentation' || isPrivileged) return t('common:join');
    if (status === 'offline') return 'Offline';
    if (meetingroom.meetingtype === 'conference' && status === 'queue') return t('booth:occupied');
    return t('booth:enter-now');
  }

  return (
    <div className="w-full flex flex-col items-center @sm:flex-row" id={meetingroom?._id}>
      <div style={{ filter: status === 'offline' ? 'grayscale(100%)' : 'none', minWidth: '64px' }}>
        <Avatar
          className="object-cover object-center w-16 h-16 rounded-full border border-black"
          src={meetingroom.image ? createDisplayLink(meetingroom.image) : '/avatar.png'}
          alt="meetingroom image"
        />
      </div>
      <div className="flex flex-col justify-between w-full h-full">
        <div className="flex flex-col justify-start ml-4 h-full" style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>
          <div className="flex">
            <p className="text-base font-bold">{meetingroom.contactperson}</p>
            {event?.settings?.showQueueIndicator && status === 'queue' && (
              <VyTooltip title={t('booth:has-queue')}>
                <HourglassEmptyOutlined fontSize="small" className="ml-1 text-red-600 animate-pulse" />
              </VyTooltip>
            )}
          </div>
          <p style={{ fontSize: '11px' }} className="text-slate-800">
            {meetingroom.contactposition || meetingroom.name} {meetingroom?.slot?.datetime_start && ` | ${getStartTime()}`}
          </p>
        </div>
        <button
          style={{ backgroundColor: event?.template.mainColor || VYSTEMBLUE, color: event?.template.mainButtonTextColor || 'white' }}
          disabled={
            !isPrivileged &&
            ((status === 'offline' && meetingroom.meetingtype !== 'presentation') || (meetingroom.meetingtype === 'conference' && status === 'queue'))
          }
          onClick={() => join(meetingroom, booth, status, true, isPrivileged)}
          className={`px-4 py-1 ml-4 text-xs rounded templateBorder ${status === 'offline' && !isPrivileged ? '!bg-slate-400 !text-white' : ''}`}
          data-cy="join-button">
          {buttonText()}
        </button>
      </div>
    </div>
  );
};

export default MeetingRoomTemplate;
